import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { DESIGN_CATEGORIES } from "../../lib/designs";

const DesignCarousel = () => {
  const [cat, setCat] = useState(DESIGN_CATEGORIES[0].id);
  const [selected, setSelected] = useState(null);
  const trackRef = useRef(null);

  const active = DESIGN_CATEGORIES.find((c) => c.id === cat);

  const scrollBy = (dir) => {
    if (trackRef.current) trackRef.current.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  return (
    <section className="py-20 md:py-24 bg-[#0A0A0A] border-t border-zinc-900" data-testid="select-design-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">Make It Yours</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mt-1">Select Your Design</h2>
          <p className="text-zinc-400 text-base md:text-lg mt-3">
            Swipe through our flake blends, stone finishes and solid colors. Found a favorite? Tell Tyler when you call.
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {DESIGN_CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => { setCat(c.id); setSelected(null); if (trackRef.current) trackRef.current.scrollTo({ left: 0 }); }}
              data-testid={`design-tab-${c.id}`}
              className={`px-5 py-2.5 text-sm font-semibold uppercase tracking-wider border transition-colors ${
                cat === c.id ? "border-[#C69453] bg-[#C69453]/10 text-white" : "border-zinc-800 text-zinc-400 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="relative">
          {/* Desktop arrows */}
          <button onClick={() => scrollBy(-1)} aria-label="Previous" data-testid="design-prev" className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center bg-black border border-zinc-700 text-white hover:border-[#C69453] hover:text-[#C69453] transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={() => scrollBy(1)} aria-label="Next" data-testid="design-next" className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center bg-black border border-zinc-700 text-white hover:border-[#C69453] hover:text-[#C69453] transition-colors">
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Swipeable track */}
          <div
            ref={trackRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-1 px-1"
            style={{ scrollbarWidth: "thin" }}
            data-testid="design-track"
          >
            {active.items.map((item) => {
              const isSel = selected === item.name;
              return (
                <button
                  key={item.name}
                  onClick={() => setSelected(item.name)}
                  data-testid={`design-swatch-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`snap-start shrink-0 w-40 sm:w-48 group text-left transition-transform ${isSel ? "scale-[1.02]" : ""}`}
                >
                  <div className={`aspect-square overflow-hidden border-2 transition-colors ${isSel ? "border-[#C69453]" : "border-zinc-800 group-hover:border-zinc-600"}`}>
                    {active.type === "image" ? (
                      <img src={item.src} alt={`${item.name} floor coating finish`} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full" style={{ backgroundColor: item.color }} />
                    )}
                  </div>
                  <p className={`mt-2 text-sm font-semibold ${isSel ? "text-[#C69453]" : "text-zinc-300"}`}>{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-8">
          {selected && (
            <p className="text-zinc-300 mb-4" data-testid="design-selected-label">
              You picked <span className="text-[#C69453] font-semibold">{selected}</span> — great choice.
            </p>
          )}
          <Link to="/contact" className="inline-flex items-center gap-2 bg-[#C69453] hover:bg-[#AE7F44] text-black font-bold px-7 py-3.5 transition-colors">
            <span>Get a Free Estimate</span><ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DesignCarousel;
